import React, { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  EButtonHierarchy,
  EButtonSize,
  EButtonStyle,
} from '@/components/common/Button/Button';
import { ExternalLink } from '@/components/common/ExternalLink/ExternalLink';
import { ReactComponent as CloseIcon } from '@/assets/icons/actions/close.svg';
import { TERMS } from '@/constants/features/links';
import { IPopupContentProps } from '@/components/common/PopupMessage/popupContent.types';
import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import { useFlagsConfig } from '@/hooks/config/useFlagsConfig.hook';
import { usePermissionsAnalytics } from '@/features/dataConsent/analytics/usePermissionsAnalytics.hook';
import { ANALYTICS } from '@/constants/analytics/analytics';
import { getCurrentScreenName } from '@/utils/analytics/checkout-analytics';
import {
  getTermsUpdatedEventPayload,
  getTermsUpdatedSource,
} from './termsUpdatedAnalytics';
import styles from './TermsUpdatedContent.module.scss';

export const TermsUpdatedContent: React.FC<IPopupContentProps> = ({
  onAction,
  onDismiss,
  close,
  showCloseButton,
  buttonHierarchy = EButtonHierarchy.primary,
}) => {
  const { t } = useTranslation();
  const { fireAnalyticsEvent } = useAnalytics();
  const { acceptTermsUpdated } = useFlagsConfig();
  const { firePermissionsEvents } = usePermissionsAnalytics();
  const hasFiredShownRef = useRef(false);

  useEffect(() => {
    if (hasFiredShownRef.current) {
      return;
    }
    hasFiredShownRef.current = true;
    fireAnalyticsEvent(
      ANALYTICS.EVENTS.TERMS_AND_CONDITIONS_POPUP,
      getTermsUpdatedEventPayload(ANALYTICS.TYPES.SHOWN, getCurrentScreenName())
    );
  }, [fireAnalyticsEvent]);

  // persist runs in the background so the modal closes instantly. a failed
  // write leaves eulaAgreed unset, so the modal simply re-shows on the next
  // eligible init rather than blocking the user behind a spinner.
  const persistAndReportConsent = async (screen: string): Promise<void> => {
    const granted = await acceptTermsUpdated();
    if (granted.length > 0) {
      firePermissionsEvents(getTermsUpdatedSource(screen), granted, true);
    }
  };

  const handleGotIt = () => {
    const screen = getCurrentScreenName();
    fireAnalyticsEvent(
      ANALYTICS.EVENTS.TERMS_AND_CONDITIONS_POPUP_CONSENT_BUTTON,
      getTermsUpdatedEventPayload(ANALYTICS.TYPES.CLICK, screen)
    );
    void persistAndReportConsent(screen);
    onAction(close);
  };

  const handleDismiss = () => {
    fireAnalyticsEvent(
      ANALYTICS.EVENTS.TERMS_AND_CONDITIONS_POPUP_CLOSE_BUTTON,
      getTermsUpdatedEventPayload(ANALYTICS.TYPES.CLICK, getCurrentScreenName())
    );
    onDismiss(close);
  };

  const handleTermsLinkClick = () => {
    fireAnalyticsEvent(
      ANALYTICS.EVENTS.TERMS_AND_CONDITIONS_POPUP_LINK,
      getTermsUpdatedEventPayload(
        ANALYTICS.TYPES.LINK_CLICK,
        getCurrentScreenName()
      )
    );
  };

  return (
    <div className={styles.content}>
      {showCloseButton && (
        <div className={styles.closeButton}>
          <Button
            size={EButtonSize.md}
            hierarchy={EButtonHierarchy.neutral}
            buttonStyle={EButtonStyle.ghost}
            iconLead={CloseIcon}
            onClick={handleDismiss}
          />
        </div>
      )}
      <div className={styles.textBlock}>
        <h2 className={styles.title}>
          <Trans i18nKey="termsUpdated.title" />
        </h2>
        <p className={styles.subtitle}>
          <Trans i18nKey="termsUpdated.subtitle" />
        </p>
      </div>
      <ExternalLink
        href={TERMS}
        text={t('termsUpdated.linkText')}
        className={styles.link}
        onClick={handleTermsLinkClick}
      />
      <Button
        onClick={handleGotIt}
        text="termsUpdated.buttonText"
        fillWidth={true}
        size={EButtonSize.md}
        hierarchy={buttonHierarchy}
      />
    </div>
  );
};
